import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, WheelEvent } from 'react';
import { useTranslation } from 'react-i18next';

import type { AffectedNode, Node } from '@/shared/api/generated/lineage';

import { NODE_HEIGHT, NODE_WIDTH, layOut, relatedTo } from '../model/graph';

/* Lineage as a flowing graph: upstream on the left, the subject in the middle, downstream on the right.
 *
 * **Pan and zoom are one `transform` on one `<g>`**, which is why this needs no library and stays smooth:
 * the browser composites a single transformed group rather than re-laying-out anything. Fit-to-screen is
 * arithmetic against the layout's own width and height.
 *
 * **Selecting a node dims everything unrelated instead of hiding it.** Hiding would make the graph
 * change shape under the reader's hands and lose the context they selected *within*; dimming keeps the
 * whole picture and answers "what does this feed" in place. Upstream and downstream get different
 * treatments, because "what am I made of" and "what breaks if I break" are the two different questions
 * a lineage graph is opened for.
 *
 * **Keyboard-reachable, which an SVG graph usually is not.** Every node is a `<g tabindex="0">` with a
 * role and an accessible name, arrow keys move along the layers, and Enter opens the detail. A graph
 * only usable with a mouse is a graph an auditor with a screen reader cannot audit — and the node list
 * is also rendered as an ordinary list below for exactly that reason, so the information is never
 * *only* in the picture.
 */

/** A dataset's health for its dot: the band its latest defensible score falls in, or `null` when it
 * has none — drawn as a hollow ring, because "never scored" is not a colour on a red-amber-green
 * scale. */
export type NodeHealth = 'ok' | 'warn' | 'error' | null;

export interface LineageGraphProps {
  readonly subject: Node;
  readonly provenance: readonly AffectedNode[];
  readonly impact: readonly AffectedNode[];
  readonly selectedId: string | null;
  readonly onSelect: (nodeId: string | null) => void;
  /** Health by dataset id. A dataset absent from the map is drawn as never scored. */
  readonly health?: ReadonlyMap<string, NodeHealth> | undefined;
}

/** A job is a pill inside its column, narrower and shorter than a dataset's box, so a pipeline step
 * reads as a step between tables rather than as a table. */
const JOB_WIDTH = 128;
const JOB_HEIGHT = 34;

type Role = 'none' | 'self' | 'up' | 'down' | 'dim';

/** What sits on the pan surface and must not start a drag. See `onPointerDown`.
 *
 * One selector rather than two checks, so a third overlay added to the frame is one entry away from
 * being correct instead of one forgotten condition away from being silently unclickable. */
const NON_PANNING = '.lq-graph__node, .lq-graph__zoom';

const MIN_SCALE = 0.4;
const MAX_SCALE = 2.4;
/** The smallest scale the graph *opens* at, as the design has it. Below this a 12px label is no
 * longer readable, so a graph too wide for the frame opens at this scale and is panned to put the
 * subject in view instead of being shrunk until nothing in it can be read. Fit still shows the whole
 * graph on request. */
const MIN_OPENING_SCALE = 0.78;
/** Where the subject sits across the frame when the graph opens wider than it: a little left of
 * centre, so more of what it feeds is in view than of what feeds it. */
const SUBJECT_ACROSS = 0.42;

export function LineageGraph({
  subject,
  provenance,
  impact,
  selectedId,
  onSelect,
  health,
}: LineageGraphProps): ReactElement {
  const { t } = useTranslation();
  const frame = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragging = useRef<{ x: number; y: number } | null>(null);

  const layout = useMemo(
    () => layOut(subject, provenance, impact),
    [subject, provenance, impact],
  );

  const related = useMemo(
    () => (selectedId === null ? null : relatedTo(layout, selectedId)),
    [layout, selectedId],
  );

  /* Where a node sits relative to the selection: the selection itself, upstream of it, downstream
     of it, or a sibling that is dimmed. With nothing selected every node is plain. */
  const roleOf = (id: string): Role =>
    related === null
      ? 'none'
      : id === selectedId
        ? 'self'
        : related.upstream.has(id)
          ? 'up'
          : related.downstream.has(id)
            ? 'down'
            : 'dim';

  /* The whole graph in the frame, centred vertically. Never scales *up* past 1 to fill the frame: a
     three-node graph blown up to fill a widescreen monitor looks like a diagram of nothing.
     An unmeasured frame (no layout yet) sets no view at all rather than one computed from zero. */
  const fit = useCallback(() => {
    const box = frame.current?.getBoundingClientRect();
    if (box === undefined || box.width === 0) return;
    const next = Math.max(
      MIN_SCALE,
      Math.min(1, box.width / layout.width, box.height / layout.height),
    );
    setScale(next);
    setPan({ x: 0, y: (box.height - layout.height * next) / 2 });
  }, [layout.width, layout.height]);

  /* The opening view, as the design computes it: fit the width when that keeps the labels readable,
     otherwise open at `MIN_OPENING_SCALE` with the subject a little left of centre. Re-run whenever
     the graph changes identity, so asking about a new node does not leave the viewport parked where
     the previous graph happened to be. */
  const open = useCallback(() => {
    const box = frame.current?.getBoundingClientRect();
    if (box === undefined || box.width === 0) return;
    const widthFit = box.width / layout.width;
    const next = Math.max(MIN_OPENING_SCALE, Math.min(1, widthFit));
    const anchor = layout.nodes.find((each) => each.id === subject.id);
    const x =
      widthFit >= MIN_OPENING_SCALE || anchor === undefined
        ? 0
        : Math.min(0, box.width * SUBJECT_ACROSS - (anchor.x + NODE_WIDTH / 2) * next);
    setScale(next);
    setPan({ x, y: (box.height - layout.height * next) / 2 });
  }, [layout, subject.id]);

  useEffect(() => {
    open();
  }, [open]);

  const onWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    /* Only with a modifier. A bare wheel over a tall page must keep scrolling the page — a graph that
       swallows the scroll is the single most complained-about behaviour in embedded diagrams. */
    if (!event.ctrlKey && !event.metaKey) return;
    event.preventDefault();
    setScale((current) =>
      Math.min(MAX_SCALE, Math.max(MIN_SCALE, current * (event.deltaY < 0 ? 1.1 : 0.9))),
    );
  }, []);

  return (
    <div className="lq-graph">
      {/* **The zoom controls are on the canvas, not above it, and that is the third position they
          have had.** Above it they were a control strip for an interaction most readers never use,
          and the reference has a bare canvas. Gone entirely they left a real gap: `fit` never scales
          *up* past 1, so a wide graph — a fan-out of five dims is 1,600px — opens shrunk to fit and
          there is no way to make it readable again. A modifier-plus-wheel is not an answer to that:
          it is undiscoverable, and on a trackpad it is a gesture people do not know they have.

          Overlaid in the corner is the convention in every map and diagram tool, and it keeps both
          properties: the picture is unframed by furniture, and the one interaction a reader
          predictably needs is one click away. The double-click refit stays as the recovery from a
          lost viewport, which is the case a button is *worse* at — by then the buttons may be the
          only thing on screen and pressing Fit is not the obvious move. */}
      <div
        className="lq-graph__frame"
        ref={frame}
        onWheel={onWheel}
        /* The recovery. `detail === 2` rather than `onDoubleClick`, because a double-click that
           landed on a node would otherwise both refit *and* toggle that node's selection — the
           second click's `click` still reaches the node. Refitting is the whole intent of the
           gesture, so the selection is left alone. */
        onDoubleClick={(event) => {
          event.preventDefault();
          fit();
        }}
        onPointerDown={(event) => {
          /* **Not when the press landed on something that sits on the pan surface.** The frame *is*
             the pan surface and both the nodes and the zoom cluster are on top of it, so without
             this every press on one of them also begins a drag.

             For a node that nudged the whole graph. For the zoom controls it was worse and is why
             they appeared not to work at all: `setPointerCapture` below redirects every later
             pointer event to the frame, so the button never gets its `pointerup` and therefore never
             gets a `click`. Pressing + or − did nothing at all — reported from the real browser, and
             **the jsdom test passed** because jsdom has no `setPointerCapture`, so the line that
             causes it threw before the capture could happen. A test that passes because the
             environment lacks the mechanism that breaks it is not evidence.

             `closest` rather than `stopPropagation` on the children, because they need their own
             clicks and swallowing the event there would break selection and zoom alike. */
          if ((event.target as Element).closest(NON_PANNING) !== null) return;
          dragging.current = { x: event.clientX - pan.x, y: event.clientY - pan.y };
          event.currentTarget.setPointerCapture(event.pointerId);
        }}
        onPointerMove={(event) => {
          const from = dragging.current;
          if (from === null) return;
          /* Guarded on the button still being held. A pointerup delivered outside the element — or
             swallowed entirely, which happens when a drag ends over a different window — would
             otherwise leave `dragging` set and make the graph follow the cursor with no button down. */
          if (event.buttons === 0) {
            dragging.current = null;
            return;
          }
          setPan({ x: event.clientX - from.x, y: event.clientY - from.y });
        }}
        onPointerUp={() => {
          dragging.current = null;
        }}
        onPointerCancel={() => {
          dragging.current = null;
        }}
        onPointerLeave={() => {
          dragging.current = null;
        }}
      >
        <svg
          className="lq-graph__svg"
          role="img"
          aria-label={t('lineage.graph.label', { name: subject.display_name })}
        >
          <g
            transform={`translate(${String(pan.x)} ${String(pan.y)}) scale(${String(scale)})`}
          >
            {layout.edges.map((edge) => {
              const from = layout.nodes.find((node) => node.id === edge.from);
              const to = layout.nodes.find((node) => node.id === edge.to);
              if (from === undefined || to === undefined) return null;
              const fromJob = from.kind === 'job';
              const toJob = to.kind === 'job';
              const x1 = from.x + (fromJob ? (NODE_WIDTH + JOB_WIDTH) / 2 : NODE_WIDTH);
              const y1 = from.y + NODE_HEIGHT / 2;
              const x2 = to.x + (toJob ? (NODE_WIDTH - JOB_WIDTH) / 2 : 0);
              const y2 = to.y + NODE_HEIGHT / 2;
              const mid = (x1 + x2) / 2;
              /* An edge takes the colour of the path it is on: upstream when both ends are on the
                 selection's upstream side, downstream likewise, dimmed when it leaves the path. */
              const a = roleOf(edge.from);
              const b = roleOf(edge.to);
              const role: Role =
                related === null
                  ? 'none'
                  : (a === 'up' || a === 'self') && (b === 'up' || b === 'self')
                    ? 'up'
                    : (a === 'down' || a === 'self') && (b === 'down' || b === 'self')
                      ? 'down'
                      : 'dim';
              return (
                <path
                  key={`${edge.from}-${edge.to}`}
                  /* A cubic with horizontal control points, so every connector leaves a node
                     horizontally and arrives horizontally. Straight diagonals across a layered graph
                     make two crossing edges impossible to follow. */
                  d={`M ${String(x1)} ${String(y1)} C ${String(mid)} ${String(y1)}, ${String(mid)} ${String(y2)}, ${String(x2 - 3)} ${String(y2)}`}
                  className="lq-graph__edge"
                  data-role={role}
                  data-inferred={from.inferred || to.inferred ? 'true' : undefined}
                  markerEnd={`url(#lq-arrow-${role})`}
                />
              );
            })}

            {layout.nodes.map((node) => {
              const isSelected = node.id === selectedId;
              const role = roleOf(node.id);
              const isJob = node.kind === 'job';
              const dot =
                node.datasetId === null ? null : (health?.get(node.datasetId) ?? null);
              const width = isJob ? JOB_WIDTH : NODE_WIDTH;
              const height = isJob ? JOB_HEIGHT : NODE_HEIGHT;
              const offsetX = isJob ? (NODE_WIDTH - JOB_WIDTH) / 2 : 0;
              const offsetY = isJob ? (NODE_HEIGHT - JOB_HEIGHT) / 2 : 0;
              return (
                <g
                  key={node.id}
                  className="lq-graph__node"
                  data-role={role}
                  transform={`translate(${String(node.x + offsetX)} ${String(node.y + offsetY)})`}
                  tabIndex={0}
                  role="button"
                  aria-pressed={isSelected}
                  /* The connection is in the accessible name, not only in the second line of
                     text: a screen reader user asking "which system is this?" must get the same
                     answer a sighted reader gets from the box. Two keys rather than one message
                     with a conditional clause, because ICU cannot select on an empty string. */
                  aria-label={
                    node.connection === null
                      ? t('lineage.graph.nodeLabel', {
                          name: node.label,
                          kind: t(`lineage.kind.${node.kind}`),
                          depth: node.depth,
                        })
                      : t('lineage.graph.nodeLabelIn', {
                          name: node.label,
                          kind: t(`lineage.kind.${node.kind}`),
                          depth: node.depth,
                          system: `${node.connection.platform} ${node.connection.name}`,
                        })
                  }
                  onClick={() => {
                    onSelect(isSelected ? null : node.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    onSelect(isSelected ? null : node.id);
                  }}
                >
                  <rect
                    width={width}
                    height={height}
                    rx={isJob ? height / 2 : 9}
                    className={
                      isJob ? 'lq-graph__box lq-graph__box--job' : 'lq-graph__box'
                    }
                    data-uncatalogued={!isJob && !node.catalogued ? 'true' : undefined}
                  />
                  {isJob ? (
                    <text
                      x={width / 2}
                      y={height / 2 + 4}
                      textAnchor="middle"
                      className="lq-graph__job"
                    >
                      {node.label.length > 16
                        ? `${node.label.slice(0, 15)}…`
                        : node.label}
                    </text>
                  ) : (
                    <>
                      {/* Health: the band of the latest defensible score, or a hollow ring for a
                          dataset nothing has scored — never a colour invented for an absence. */}
                      <circle
                        cx={14}
                        cy={18}
                        r={4}
                        className="lq-graph__health"
                        data-health={dot ?? 'none'}
                      />
                      <text x={26} y={22} className="lq-graph__name">
                        {node.label.length > 22
                          ? `${node.label.slice(0, 21)}…`
                          : node.label}
                      </text>
                      <text
                        x={26}
                        y={39}
                        className={
                          node.connection === null
                            ? 'lq-graph__system lq-graph__system--none'
                            : 'lq-graph__system'
                        }
                      >
                        {node.connection === null
                          ? t('lineage.graph.notCatalogued')
                          : node.connection.name.length > 24
                            ? `${node.connection.name.slice(0, 23)}…`
                            : node.connection.name}
                      </text>
                    </>
                  )}
                </g>
              );
            })}
          </g>

          <defs>
            {(['none', 'up', 'down', 'dim', 'self'] as const).map((role) => (
              <marker
                key={role}
                id={`lq-arrow-${role}`}
                viewBox="0 0 8 8"
                refX="7"
                refY="4"
                markerWidth="7"
                markerHeight="7"
                orient="auto"
              >
                <path d="M0 0L8 4L0 8z" className="lq-graph__arrow" data-role={role} />
              </marker>
            ))}
          </defs>
        </svg>

        {/* Inside the frame so they float over the canvas, and **after** the `<svg>` so they paint
            above it without needing a `z-index`. `aria-label` on each rather than trusting the glyph:
            "+" and "−" are not names, and a screen reader announcing "plus button" says nothing about
            what it does. */}
        <div className="lq-graph__zoom">
          <button
            type="button"
            className="lq-graph__zoom-button"
            aria-label={t('lineage.graph.zoomIn')}
            title={t('lineage.graph.zoomIn')}
            onClick={() => {
              setScale((current) => Math.min(MAX_SCALE, current * 1.2));
            }}
          >
            +
          </button>
          <button
            type="button"
            className="lq-graph__zoom-button"
            aria-label={t('lineage.graph.zoomOut')}
            title={t('lineage.graph.zoomOut')}
            onClick={() => {
              setScale((current) => Math.max(MIN_SCALE, current / 1.2));
            }}
          >
            −
          </button>
          <button
            type="button"
            className="lq-graph__zoom-button"
            aria-label={t('lineage.graph.fit')}
            title={t('lineage.graph.fit')}
            onClick={fit}
          >
            {/* A frame glyph rather than the word, so the cluster stays three equal squares. The
                accessible name and the tooltip both say "Fit to screen". */}
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
              <path
                d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        <span className="lq-graph__panHint">{t('lineage.graph.panHint')}</span>
      </div>
    </div>
  );
}
